import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { SHOP_CURRENCIES, ShopCurrencySchema } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  RHF,
  Switch,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { z } from "zod";
import { useCreateShop } from "../api/queries";

const SHOP_CURRENCY_OPTIONS = SHOP_CURRENCIES.map((c) => ({
  value: c,
  label: c,
}));

const ShopCreateSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຮ້ານ"),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  taxId: z.string().max(64).optional(),
  defaultTaxPercent: z.coerce
    .number()
    .min(0)
    .max(999.99)
    .optional()
    .default(0),
  defaultCurrency: ShopCurrencySchema.default("LAK"),
  enforceSingleShopSkuCurrency: z.boolean().optional().default(false),
});

type ShopCreateValues = z.infer<typeof ShopCreateSchema>;

export function ShopCreatePage() {
  const nav = useNavigate({ from: "/app/shops/create" });
  const createShop = useCreateShop();

  const methods = RHF.useForm<ShopCreateValues>({
    resolver: zodResolver(ShopCreateSchema) as RHF.Resolver<ShopCreateValues>,
    defaultValues: {
      name: "",
      phone: "",
      address: "",
      city: "",
      taxId: "",
      defaultTaxPercent: 0,
      defaultCurrency: "LAK",
      enforceSingleShopSkuCurrency: false,
    },
  });

  const onSubmit = async (vals: ShopCreateValues) => {
    try {
      const shop = await createShop.mutateAsync({
        name: vals.name,
        phone: vals.phone || null,
        address: vals.address || null,
        city: vals.city || null,
        taxId: vals.taxId?.trim() || null,
        defaultTaxPercent: vals.defaultTaxPercent ?? 0,
        defaultCurrency: vals.defaultCurrency,
        enforceSingleShopSkuCurrency: vals.enforceSingleShopSkuCurrency ?? false,
      });
      toast.success("ສ້າງຮ້ານສຳເລັດ", {
        description: "ຮ້ານຖືກສ້າງແລ້ວ",
      });
      nav({ to: "/app/shops/$id", params: { id: shop.id } });
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            { label: "ສ້າງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ເພີ່ມຮ້ານ</h2>
            <p className="text-muted-foreground">ສ້າງຮ້ານຄ້າໃໝ່ໃນແຜງ.</p>
          </div>

          <Button variant="outline" onClick={() => nav({ to: "/app/shops" })}>
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot<ShopCreateValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput
                name="name"
                requiredMark
                label="ຊື່ຮ້ານ"
                placeholder="ຊື່ຮ້ານສະແດງ"
              />
              <FormInput
                name="phone"
                label="ເບີໂທ"
                placeholder="+856 20 xxxx xxxx"
              />
            </div>
            <FormInput name="address" label="ທີ່ຢູ່" placeholder="ບ້ານ ເມືອງ ແຂວງ" />
            <FormInput name="city" label="ເມືອງ / ແຂວງ" placeholder="ວຽງຈັນ" />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput
                name="taxId"
                label="ເລກປະຈຳຕົວຜູ້ເສຍພາສີ"
                placeholder="ສຳລັບໃບກຳກັບ"
              />
              <FormInput
                name="defaultTaxPercent"
                type="number"
                step="0.01"
                min={0}
                label="ອັດຕາພາສີເລີ່ມຕົ້ນ POS (%)"
                placeholder="0"
              />
            </div>
            <FormSelect
              name="defaultCurrency"
              label="ສະກຸນເງິນເລີ່ມຕົ້ນ (POS / SKU)"
              options={SHOP_CURRENCY_OPTIONS}
            />
            <div className="flex items-center gap-2">
              <RHF.Controller
                control={methods.control}
                name="enforceSingleShopSkuCurrency"
                render={({ field }) => (
                  <Switch
                    id="create-shop-enforce-sku-currency"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="data-[state=checked]:bg-[#F26522]"
                  />
                )}
              />
              <label
                htmlFor="create-shop-enforce-sku-currency"
                className="font-medium text-sm"
              >
                ເປີດນຳໃຊ້ ຫຼາຍສະກຸນເງິນ ໃນຄີສິນຄ້າເຂົ້າ
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/shops" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" isLoading={createShop.isPending}>
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
